import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFriends from '@/hooks/contacts/use-friends';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import type { GroupMember } from '@/models/group/group-info';
import type { MentionSource } from '@/services/quill';
import { useUserId } from '@/stores/auth';

export const generateGroupMembersQueryOptions = (groupId: Nullable<number>) =>
  queryOptions({
    ...generateGroupInfoQueryOptions(groupId),
    select: (data) => {
      const members = data.Members ?? [];

      return {
        memberCount: members.length,
        members: members.reduce<Record<number, GroupMember>>((accu, member) => {
          accu[member.UserID] = member;
          return accu;
        }, {}),
        bannedMembers: members.filter((m) => m.Status === 0),
      };
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export default function useGroupMembers(groupId: Nullable<number>) {
  const query = useQuery(generateGroupMembersQueryOptions(groupId));

  return {
    ...query,
    memberCount: query.data?.memberCount ?? 0,
    members: query.data?.members ?? {},
    bannedMembers: query.data?.bannedMembers ?? [],
  };
}

export function useGroupMembersWithRemarkName(groupId: Nullable<number>) {
  const { friends } = useFriends();
  const query = useQuery(generateGroupMembersQueryOptions(groupId));

  const members = useMemo(() => {
    const members = query.data?.members;
    if (!members) return [];

    return Object.values(members).map((member) => {
      const friend = friends[member.UserID];
      return {
        ...member,
        RemarkName: friend?.RemarkName ?? '',
      };
    });
  }, [query.data, friends]);

  return {
    ...query,
    members,
  };
}

export function useGroupMentionSources(groupId: Nullable<number>) {
  const { t } = useTranslation();
  const userId = useUserId();
  const query = useGroupMembersWithRemarkName(groupId);

  const sources = useMemo(() => {
    if (!query.members) return [];

    const sources: MentionSource[] = [];
    for (const member of query.members) {
      if (member.UserID === userId) continue;

      sources.push({
        id: member.UserID,
        value: member.GroupMemberName || member.UserNickName || `${member.UserID}`,
        image: member.UserHeadImg,
        remarkName: member.RemarkName,
        role: member.Role,
      });
    }

    sources.unshift({
      id: 0,
      value: t('conversation.mention.all'),
      image: '',
      role: 0,
    });

    return sources;
  }, [query.members, userId]);

  return {
    sources,
    ...query,
  };
}

export function useGroupMember(groupId: Nullable<number>, userId: Nullable<number>) {
  return useQuery({
    ...generateGroupInfoQueryOptions(groupId),
    enabled: Boolean(groupId && userId),
    select: (data) => data.Members?.find((m) => m.UserID === userId),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
